/**
 * „Twoje tagi" above the table — the trainer's own tags as toggle chips (exercise-library R23,
 * canon `F2-lista` of the 10 October 2026 tag boards).
 *
 * A press selects or drops a tag, and the table shows every movement carrying ANY of the selected
 * ones, combined with the other filters as both. The selection is the same value the filter panel's
 * „Tagi" tab writes, so a chip lit here is a box ticked there and the reverse.
 *
 * ── Not drawn at all for a trainer with no tags ────────────────────────────────────────────
 * The canon's own note. It is drawn inside the list's content branch, not above the wait: the
 * catalogue is what says whether there are tags, so the bar arrives with the rows in the one
 * hand-over and never pushes a table that is already standing.
 *
 * ── One tab stop for the chips, however many there are ─────────────────────────────────────
 * A trainer may hold two hundred tags, and a tab stop per chip put two hundred presses between the
 * table and the toolbar. The chips are a `toolbar`: one tab stop on the chip last moved to, the
 * arrows (and Home / End) between them, „Wyczyść" and „Zarządzaj" one Tab further. „Wyczyść"
 * disappears with the selection it clears, so it hands focus to the first chip first.
 *
 * ── The chips scroll sideways rather than clip ─────────────────────────────────────────────
 * A trainer may hold two hundred tags. The canon's bar is one line with `overflow: hidden`, which
 * at that count is a clip with no scroll path, so the chips sit in their own horizontal scroller
 * and „Wyczyść" and „Zarządzaj" stay pinned at the end of the line.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ExerciseTagOption } from '~/entities/exercise-library';
import shell from '~/shared/ui/filterPanel.module.css';
import scrollbar from '~/shared/ui/scrollbar.module.css';

import { TagGlyph } from './ExerciseLibraryIcons';
import styles from './ExerciseTagBar.module.css';

interface ExerciseTagBarProps {
  readonly tags: readonly ExerciseTagOption[];
  readonly onToggle: (key: string) => void;
  readonly onClear: () => void;
  readonly onManage: () => void;
  /** The list's hand-over class: the bar arrives with the rows that replaced the skeleton. */
  readonly className: string;
}

export function ExerciseTagBar({
  tags,
  onToggle,
  onClear,
  onManage,
  className,
}: ExerciseTagBarProps) {
  const { t } = useTranslation();
  const anySelected = tags.some((tag) => tag.isSelected);
  const chips = useRef<HTMLDivElement>(null);
  // The roving stop, by key so it survives a count changing; a key that left the list falls back
  // to the first chip.
  const [stopKey, setStopKey] = useState<string | null>(null);
  const stop = Math.max(
    0,
    tags.findIndex((tag) => tag.value === stopKey),
  );

  const focusChip = (index: number) => {
    const tag = tags[index];
    if (tag === undefined) return;
    setStopKey(tag.value);
    chips.current?.querySelectorAll<HTMLButtonElement>('button')[index]?.focus();
  };

  return (
    <div
      className={`${String(styles.bar)} ${className}`}
      role="group"
      aria-label={t('web:exerciseLibrary.tags.title')}
    >
      <TagGlyph className={styles.glyph} />
      <span className={styles.label} aria-hidden>
        {t('web:exerciseLibrary.tags.title')}
      </span>
      <div
        ref={chips}
        className={`${String(scrollbar.scrollbar)} ${String(styles.chips)}`}
        role="toolbar"
        aria-label={t('web:exerciseLibrary.tags.filterBy')}
        onKeyDown={(event) => {
          const next =
            event.key === 'ArrowRight'
              ? Math.min(stop + 1, tags.length - 1)
              : event.key === 'ArrowLeft'
                ? Math.max(stop - 1, 0)
                : event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? tags.length - 1
                    : null;
          if (next === null) return;
          event.preventDefault();
          focusChip(next);
        }}
      >
        {tags.map((tag, index) => (
          <button
            key={tag.value}
            type="button"
            tabIndex={index === stop ? 0 : -1}
            aria-pressed={tag.isSelected}
            className={`${String(styles.chip)} ${tag.isSelected ? String(styles.chipOn) : ''}`}
            onClick={() => {
              setStopKey(tag.value);
              onToggle(tag.value);
            }}
          >
            {tag.label}
            <i className={styles.count}>{tag.count}</i>
          </button>
        ))}
      </div>
      {anySelected && (
        <button
          type="button"
          className={shell.clear}
          onClick={() => {
            // Moved BEFORE the clear: this button is gone in the render the clear causes.
            focusChip(0);
            onClear();
          }}
        >
          {t('web:exerciseLibrary.filters.clear')}
        </button>
      )}
      <button type="button" className={`${String(shell.clear)} ${String(styles.manage)}`} onClick={onManage}>
        {t('web:exerciseLibrary.tags.manage')}
      </button>
    </div>
  );
}
